import { useEffect } from "react";
import { COMPANY, PHONE, PHONE_INTL, EMAIL, ADDRESSES, IMAGES, SITE_URL } from "./api";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const OPENING_HOURS = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  opens: "00:00",
  closes: "23:59",
};

export function useSEO({ title, description, path = "/", faq = null, service = null, city = null, keywords = null }) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const ogImage = IMAGES?.hero || "";

    if (title) document.title = title;
    setMeta("description", description);
    if (keywords && keywords.length) {
      setMeta("keywords", keywords.join(", "));
    }

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", url, "property");
    setMeta("og:site_name", COMPANY, "property");
    setMeta("og:locale", "fr_FR", "property");
    if (ogImage) setMeta("og:image", ogImage, "property");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) setMeta("twitter:image", ogImage);

    // Geo + robots : seul le domaine canonique (production) est indexable.
    // Les autres domaines (ancien .emergent.host, preview) émettent noindex pour éviter les doublons.
    let canonicalHost = "";
    try { canonicalHost = new URL(SITE_URL).host; } catch (e) { canonicalHost = ""; }
    const isCanonicalHost = typeof window !== "undefined" && window.location.hostname === canonicalHost;
    setMeta("robots", isCanonicalHost ? "index, follow" : "noindex, follow");
    setMeta("geo.region", "FR-77");
    setMeta("geo.placename", "Seine-et-Marne");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // LocalBusiness (Locksmith) with the 3 real branches
    const branches = (ADDRESSES || []).map((a, i) => ({
      "@type": "Locksmith",
      "@id": `${SITE_URL}/#branch-${i}`,
      name: `${COMPANY} — ${a.city}`,
      telephone: PHONE_INTL,
      email: EMAIL,
      image: ogImage || undefined,
      priceRange: "€€",
      address: {
        "@type": "PostalAddress",
        streetAddress: a.street,
        postalCode: a.cp,
        addressLocality: a.city,
        addressRegion: "Seine-et-Marne",
        addressCountry: "FR",
      },
      areaServed: { "@type": "AdministrativeArea", name: "Seine-et-Marne (77)" },
      openingHoursSpecification: OPENING_HOURS,
    }));

    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "Locksmith",
      "@id": `${SITE_URL}/#organization`,
      name: COMPANY,
      telephone: PHONE_INTL,
      email: EMAIL,
      url: SITE_URL,
      image: ogImage || undefined,
      description: "Serrurerie 77 est un serrurier professionnel intervenant en urgence 7j/7 et 24h/24 dans toute la Seine-et-Marne (77) : dépannage, ouverture de porte, changement de serrure, blindage et mise en sécurité. Devis gratuit, intervention rapide, serrures certifiées A2P.",
      slogan: "Votre serrurier d'urgence en Seine-et-Marne, 7j/7 et 24h/24",
      knowsAbout: ["serrurerie", "dépannage serrurier", "ouverture de porte", "changement de serrure", "blindage de porte", "serrure A2P", "mise en sécurité", "réparation après effraction"],
      priceRange: "€€",
      currenciesAccepted: "EUR",
      paymentAccepted: "Espèces, Carte bancaire, Chèque",
      areaServed: { "@type": "AdministrativeArea", name: "Seine-et-Marne (77)" },
      address: (ADDRESSES || []).map((a) => ({
        "@type": "PostalAddress",
        streetAddress: a.street,
        postalCode: a.cp,
        addressLocality: a.city,
        addressRegion: "Seine-et-Marne",
        addressCountry: "FR",
      })),
      openingHoursSpecification: OPENING_HOURS,
      location: branches,
    };
    setJsonLd("ld-business", localBusiness);

    if (service) {
      setJsonLd("ld-service", {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: service.name,
        provider: { "@type": "Locksmith", name: COMPANY, telephone: PHONE_INTL },
        areaServed: { "@type": "AdministrativeArea", name: "Seine-et-Marne (77)" },
        description: service.intro,
      });
    } else {
      const s = document.getElementById("ld-service");
      if (s) s.remove();
    }

    if (faq && faq.length) {
      setJsonLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    } else {
      const f = document.getElementById("ld-faq");
      if (f) f.remove();
    }
  }, [title, description, path, faq, service, city, keywords]);
}
